import React, { PureComponent } from "react";
import PropTypes from "prop-types";
import queryString from "query-string";
import { withRouter } from "react-router";
import { inRange, clamp, union, pickBy, mapKeys } from "lodash";

const DEFAULT_PAGE_SIZE = 10;
const DEFAULT_FIRST_PAGE = 1;

function withQueryParamsHandler(WrappedComponent) {
  class WrapperComponent extends PureComponent {
    allQueryParams = {};
    relatedQueryParams = {};

    rememberQueryParams = () => {
      const {
        location: { search },
      } = this.props;
      this.allQueryParams = queryString.parse(search);
      this.relatedQueryParams = this.filterQueryParamsBySourceType(
        this.allQueryParams,
      );
    };

    filterQueryParamsBySourceType = queryParams => {
      const { sourceType } = this.props;

      const queryParamsWithSourceType = pickBy(
        queryParams,
        (queryValue, queryKey) => queryKey.startsWith(sourceType),
      );

      return mapKeys(queryParamsWithSourceType, (queryValue, queryKey) =>
        queryKey.replace(sourceType, ""),
      );
    };

    componentDidMount() {
      this.rememberQueryParams();
      this.loadData();
    }

    componentDidUpdate({ location: { search: prevSearch } }) {
      if (prevSearch !== this.props.location.search) {
        this.rememberQueryParams();

        if (this.isCurrentPageOutOfRange()) {
          const page = this.getLastAvailablePage();
          this.loadData({ page });
        } else if (this.isAnyParamChanged(prevSearch)) {
          this.loadData();
        }
      }
    }

    /**
     * Checks if an existing page when out of range.
     */
    isCurrentPageOutOfRange = () => {
      const { totalEntities } = this.props;
      const {
        page = DEFAULT_FIRST_PAGE,
        size = DEFAULT_PAGE_SIZE,
      } = this.relatedQueryParams;
      const totalPages = Math.ceil(totalEntities / size);

      return !inRange(page, 1, totalPages + 1);
    };

    getLastAvailablePage = () => {
      const { totalEntities } = this.props;
      const {
        page = DEFAULT_FIRST_PAGE,
        size = DEFAULT_PAGE_SIZE,
      } = this.relatedQueryParams;
      const totalPages = Math.ceil(totalEntities / size);

      return clamp(page, 1, totalPages);
    };

    isAnyParamChanged = prevSearch => {
      const prevQueryParams = queryString.parse(prevSearch);
      const relatedPrevQueryParams = this.filterQueryParamsBySourceType(
        prevQueryParams,
      );

      const keysOfParams = union(
        Object.keys(relatedPrevQueryParams),
        Object.keys(this.relatedQueryParams),
      );

      return keysOfParams.some(
        key => relatedPrevQueryParams[key] !== this.relatedQueryParams[key],
      );
    };

    loadData = (overwrittenParams = {}) => {
      const {
        page = DEFAULT_FIRST_PAGE,
        size = DEFAULT_PAGE_SIZE,
        filter,
        sort,
        type,
      } = this.relatedQueryParams;
      this.props.onLoadData({
        page: Number.isInteger(Number(page)) ? page - 1 : DEFAULT_FIRST_PAGE,
        size: Number.isInteger(Number(size)) ? size : DEFAULT_PAGE_SIZE,
        filter,
        sort,
        type,
        ...overwrittenParams,
      });
    };

    onChangeQueryParams = (newQueryParams, isReplacement = false) => {
      const { history, location } = this.props;

      const changedQueryParams = this.addSourceTypeToQueryParams(
        newQueryParams,
      );
      const newSearchQuery = queryString.stringify({
        ...this.allQueryParams,
        ...changedQueryParams,
      });
      const path = { ...location, search: newSearchQuery };

      if (isReplacement) history.replace(path);
      else history.push(path);
    };

    addSourceTypeToQueryParams = queryObject => {
      const { sourceType } = this.props;
      const queryArray = Object.entries(queryObject).map(([key, value]) => [
        sourceType + key,
        value,
      ]);

      return Object.fromEntries(queryArray);
    };

    render() {
      const { forwardRef } = this.props;

      return (
        <WrappedComponent
          {...this.props}
          ref={forwardRef}
          queryParams={this.relatedQueryParams}
          onChangeQueryParams={this.onChangeQueryParams}
          loadData={this.loadData}
        />
      );
    }
  }

  WrapperComponent.propTypes = {
    onLoadData: PropTypes.func.isRequired,
    isLoading: PropTypes.bool.isRequired,
    data: PropTypes.array.isRequired,
    totalEntities: PropTypes.number.isRequired,
    columns: PropTypes.array.isRequired,
    sourceType: PropTypes.string.isRequired,
    acceptsType: PropTypes.string.isRequired,
    onDrop: PropTypes.func.isRequired,
  };

  WrapperComponent.defaultProps = {
    totalEntities: 0,
  };

  return withRouter(WrapperComponent);
}

export default withQueryParamsHandler;
